import { HouseIcon, SuitcaseIcon } from '@phosphor-icons/react'
import { TFunction } from 'i18next'
import z from 'zod'

import { getPersonColumn, PersonColumnSettings } from '@/components/business/person-column'
import { Badge } from '@/components/ui/badge'
import { Icon } from '@/components/ui/icon'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { SortOrderSchema, TableColumn } from '@/components/ui/table'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants'
import { GetPackageColumnsParams, PackageModel } from '@/models'
import { getPackageBadgeColor, getPackageStatusLabels } from '@/pages/packages'
import * as S from '@/pages/packages/packages-list/packages-list.styled'
import {
  formatDateOnly,
  formatTimeOnly,
  getCarrierName,
  getDeliveryTypeName,
  getResidentUnitDisplay,
} from '@/utils'

import { PackageStatusExtendedEnum } from './packages-list.types'

export const PACKAGES_LIST_ID = 'packages-table'

export const ADDITIONAL_FILTERS_DROPDOWN_WIDTH = '32.8rem'

// Note: Packages' "Timeline" - time first so a long email after it can't truncate it away
const formatPackageTimeline = (timestamp: number) =>
  `${formatTimeOnly(timestamp)} ${formatDateOnly(timestamp)}`

export const getPackageColumns = ({
  selectedBuilding,
  t,
  residentColumnSettings,
  checkedInColumnSettings,
  checkedOutColumnSettings,
}: GetPackageColumnsParams & {
  t: TFunction<['packages']>
  residentColumnSettings: PersonColumnSettings
  checkedInColumnSettings: PersonColumnSettings
  checkedOutColumnSettings: PersonColumnSettings
}): TableColumn<PackageModel>[] => [
  {
    id: 'id',
    render: (packageItem) => (
      <Typography as="span" size="text-small" color="black800">
        {packageItem.id}
      </Typography>
    ),
    title: t('packages:list.columns.id'),
    width: '12rem',
  },
  getPersonColumn(checkedInColumnSettings, {
    getDisplay: (row) => row.checkedInDisplay,
    getLeadingDetail: ({ checkinAt }) => (checkinAt ? formatPackageTimeline(checkinAt) : null),
    getPerson: (packageItem) =>
      packageItem.checkinByFirstName || packageItem.checkinByName
        ? {
            avatar: packageItem.checkinByAvatar,
            email: packageItem.checkinBy,
            firstName: packageItem.checkinByFirstName ?? packageItem.checkinByName,
            kind: 'staff',
            lastName: packageItem.checkinByFirstName ? packageItem.checkinByLastName : null,
            role: packageItem.checkinByRole,
          }
        : null,
    id: 'checkedIn',
    pageName: t('packages:list.pageName'),
    renderFallback: ({ checkinAt }) => (checkinAt ? formatPackageTimeline(checkinAt) : EMPTY_VALUE),
    sortKey: 'checkinAt',
    source: 'packages',
    title: t('packages:list.columns.checkedIn'),
    sortAscLabel: t('packages:list.columns.checkedInSortAsc'),
    sortDescLabel: t('packages:list.columns.checkedInSortDesc'),
  }),
  getPersonColumn(checkedOutColumnSettings, {
    getDisplay: (row) => row.checkedOutDisplay,
    getLeadingDetail: ({ checkoutAt }) => (checkoutAt ? formatPackageTimeline(checkoutAt) : null),
    getPerson: (packageItem) =>
      packageItem.checkoutByFirstName || packageItem.checkoutByName
        ? {
            avatar: packageItem.checkoutByAvatar,
            email: packageItem.checkoutBy,
            firstName: packageItem.checkoutByFirstName ?? packageItem.checkoutByName,
            kind: 'staff',
            lastName: packageItem.checkoutByFirstName ? packageItem.checkoutByLastName : null,
            role: packageItem.checkoutByRole,
          }
        : null,
    id: 'checkedOut',
    pageName: t('packages:list.pageName'),
    renderFallback: ({ checkoutAt }) =>
      checkoutAt ? formatPackageTimeline(checkoutAt) : EMPTY_VALUE,
    sortKey: 'checkoutAt',
    source: 'packages',
    title: t('packages:list.columns.checkedOut'),
    sortAscLabel: t('packages:list.columns.checkedOutSortAsc'),
    sortDescLabel: t('packages:list.columns.checkedOutSortDesc'),
  }),
  getPersonColumn(residentColumnSettings, {
    getDisplay: (row) => row.residentDisplay,
    getPerson: (packageItem) =>
      packageItem.residentFirstName || packageItem.residentName
        ? {
            apartment: packageItem.residentUnit,
            avatar: packageItem.residentAvatar,
            buildingNumber: packageItem.residentBuildingNumber,
            clientId: packageItem.clientId,
            email: packageItem.residentEmail,
            firstName: packageItem.residentFirstName ?? packageItem.residentName,
            kind: 'resident',
            lastName: packageItem.residentFirstName ? packageItem.residentLastName : null,
            phone: packageItem.residentPhone,
            street: packageItem.residentStreet,
          }
        : null,
    id: 'residentName',
    pageName: t('packages:list.pageName'),
    renderExtra: ({
      residentName,
      isResidentOnVacation,
      vacationNote,
      vacationEndDate,
      vacationStartDate,
      client,
    }) => (
      <>
        {client?.inHouse && (
          <Tooltip
            placement="right"
            trigger={
              <S.InHouseIconWrapper>
                <Icon icon={HouseIcon} size="1.6rem" color="green500" />
              </S.InHouseIconWrapper>
            }
          >
            {t('packages:list.inHouseDelivery')}
          </Tooltip>
        )}
        {isResidentOnVacation && vacationStartDate && vacationEndDate && (
          <Tooltip
            placement="right"
            trigger={
              <S.VacationIconWrapper>
                <Icon icon={SuitcaseIcon} size="1.6rem" color="red500" />
              </S.VacationIconWrapper>
            }
          >
            <div>
              <div>
                {t('packages:list.vacationPeriod', {
                  endDate: formatDateOnly(vacationEndDate),
                  name: residentName,
                  startDate: formatDateOnly(vacationStartDate),
                })}
              </div>
              <div>{t('packages:list.vacationNote', { note: vacationNote || EMPTY_VALUE })}</div>
            </div>
          </Tooltip>
        )}
      </>
    ),
    source: 'packages',
    title: t('packages:list.columns.resident'),
  }),
  {
    id: 'unit',
    render: (packageItem) => (
      <Typography as="span" color="black800" size="text-small">
        {getResidentUnitDisplay(packageItem, selectedBuilding)}
      </Typography>
    ),
    sortKey: 'residentUnit',
    title: t('packages:list.columns.unit'),
    width: '12rem',
  },
  {
    id: 'trackingNumber',
    render: (packageItem) => (
      <Typography as="div" color="black800" size="text-small" breakLines={1}>
        {packageItem.trackingNumber || EMPTY_VALUE}
      </Typography>
    ),
    title: t('packages:list.columns.trackingNumber'),
    width: '18rem',
  },
  {
    id: 'carrier',
    render: (packageItem) => (
      <Typography as="span" color="black800" size="text-small">
        {getCarrierName(packageItem.carrier)}
      </Typography>
    ),
    title: t('packages:list.columns.carrier'),
    width: '12rem',
  },
  {
    id: 'location',
    render: (packageItem) => (
      <Typography as="span" color="black800" size="text-small" weight="medium" breakLines={1}>
        {packageItem.location || EMPTY_VALUE}
      </Typography>
    ),
    title: t('packages:list.columns.location'),
    width: '18rem',
  },
  {
    id: 'perishable',
    render: (packageItem) => (
      <Typography as="span" color="black800" size="text-small">
        {packageItem.perishable ? t('packages:yesNo.yes') : t('packages:yesNo.no')}
      </Typography>
    ),
    sortAscLabel: t('packages:list.columns.perishableSortAsc'),
    sortDescLabel: t('packages:list.columns.perishableSortDesc'),
    sortKey: 'perishable',
    title: t('packages:list.columns.perishable'),
    width: '16rem',
  },
  {
    id: 'status',
    render: (packageItem) => {
      const { status, deliveryType } = packageItem

      if (!status) {
        return EMPTY_VALUE
      }

      return (
        <S.StatusWrapper>
          <Badge color={getPackageBadgeColor(status)} variant="light" size="small">
            {getPackageStatusLabels(t)[status]}
            {status === 'Requested' && ` (${getDeliveryTypeName(deliveryType)})`}
          </Badge>
        </S.StatusWrapper>
      )
    },
    title: t('packages:list.columns.status'),
    width: '26rem',
  },
]

export const packagesQueryFiltersSchema = z.object({
  carrier: z.array(z.coerce.string()).optional(),
  clientId: z.array(z.coerce.number()).optional(),
  fromDate: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
  locations: z.array(z.string()).optional(),
  perishable: z.string().optional(),
  search: z.string().optional(),
  skip: z.coerce.number().optional(),
  sortBy: z.string().optional(),
  sortOrder: SortOrderSchema.optional(),
  status: PackageStatusExtendedEnum.optional(),
  toDate: z.coerce.number().optional(),
})

export type PackagesFilters = z.infer<typeof packagesQueryFiltersSchema>

export const initialPackagesFilters: PackagesFilters = {
  carrier: [],
  clientId: [],
  fromDate: undefined,
  limit: DEFAULT_LIMIT,
  locations: [],
  perishable: undefined,
  search: '',
  skip: 0,
  sortBy: undefined,
  sortOrder: undefined,
  status: PackageStatusExtendedEnum.enum.All,
  toDate: undefined,
}

export const getPerishableFilterOptions = (t: TFunction<['packages']>) => [
  { label: t('packages:list.filters.perishable.options.yes'), value: 'true' },
  { label: t('packages:list.filters.perishable.options.no'), value: 'false' },
]
